/**
 * Ручки для консоли: `airel.` и дальше по имени.
 *
 * Почти всё, что здесь лежит, — временное и про настройку звука на слух.
 * Числа, которые подбираются ухом посреди разговора, органом управления не
 * сделать: пока неизвестно, какое число ставить, неизвестно и что рисовать в
 * макете. Когда числа выберутся, отсюда уйдёт и ручка. Журнал и замер
 * плавности — не временные: их просят выполнить на чужой машине.
 *
 * Одной точкой, а не по объявлению в каждом модуле: `window.airel` — одно
 * поле, и два разных его описания TypeScript просто не сложит.
 */

import type { GateReport, LevelerReport } from "@shared/denoiser";
import { path as journalPath, text as journalText } from "@shared/lib/journal";
import { callReport, micReport, type MicReport, type StreamReport } from "@shared/media";

interface Console {
  /**
   * Предел подавления шумодава, децибелы. Без числа — что стоит сейчас.
   *
   * Меняется на живой модели: пересоздавать её ради этого нельзя — полсекунды
   * на разбор весов рвут сравнение, а сравнивать надо на одном и том же
   * голосе.
   */
  attenuation(value?: number): Promise<number>;
  /**
   * Что затвор думает прямо сейчас: открыт ли, уровень, шумовой пол, порог.
   *
   * Отвечает на единственный вопрос, который о нём задают, — почему он не
   * открылся (или почему открыт). Пусто, если микрофон не включён: тракта
   * тогда нет, и спрашивать некого.
   */
  gate(): Promise<GateReport | null>;
  /**
   * Что делает выравниватель: какой уровень речи выучил, где шумовой пол и
   * сколько прибавляет. Нужен, когда жалуются на тихого или на «качающийся»
   * голос: по этим числам видно, тихий ли микрофон на самом деле и не принял
   * ли выравниватель шум за речь. Пусто без микрофона или на ступени
   * подавления «Выключено».
   */
  leveler(): Promise<LevelerReport | null>;
  /** Что отдаёт микрофон до нашей обработки. */
  mic(): Promise<MicReport>;
  /**
   * Что делает с голосом дорога: кодек, скорость, потери, заклейка.
   *
   * Занимает три секунды: скорость и потери — величины за окно, из одного
   * снимка их не достать. Снимать на слушающей стороне.
   */
  stats(): Promise<StreamReport[]>;
  /**
   * Журнал целиком, одной строкой. Отсюда его копируют и пересылают.
   *
   * В отличие от остального в этом наборе — не временная ручка: журнал нужен
   * тогда, когда что-то не работает у другого человека, а самый короткий
   * способ забрать его из чужой машины — «выполни это в консоли и пришли
   * ответ».
   */
  journal(): string;
  /** Где лежит файл журнала. Пусто в браузере — там его нет. */
  journalPath(): Promise<string | null>;
  /**
   * Замер плавности стекла: шесть сцен по пять секунд, ответ — таблицей
   * (кадры в секунду, промежутки между кадрами), в буфер обмена и в журнал.
   * Начинается через `delay` мс, по умолчанию через три секунды.
   */
  perf(delay?: number): Promise<string>;
}

declare global {
  interface Window {
    airel?: Console;
  }
}

/*
 * Шумодав приезжает по требованию, а не при запуске.
 *
 * Ручки здесь дёргают руками из отладчика, и ради двух из них обвязка
 * шумодава ехала в тот кусок, что грузится при открытии страницы, — всем, кто
 * в этот раз даже не заговорит.
 *
 * Плата — обещание вместо числа: `attenuation` теперь отвечает так же, как
 * `gate`. В отладчике это незаметно, он разворачивает обещания сам.
 */
export function holdConsole(): void {
  window.airel = {
    attenuation: async (value?: number) => (await import("@shared/denoiser")).setAttenuation(value),
    gate: async () => (await import("@shared/denoiser")).gateReport(),
    leveler: async () => (await import("@shared/denoiser")).levelerReport(),
    mic: micReport,
    stats: callReport,
    journal: journalText,
    journalPath,
    perf: async (delay?: number) => (await import("./perf")).perf(delay),
  };
}
